import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { AppButton } from '../AppButton/AppButton';

interface Option {
  value: string;
  label: string;
  icon?: any;
  extraData?: any;
}

interface Props {
  options: Option[];
  onSelect: (option: Option) => void;
  buttonTitle: string;
  icon?: any;
  contentWidth?: number;
  buttonType?: 'PRIMARY' | 'SECONDARY' | 'SECONDARY2' | 'FLAT' | 'TERTIARY' | 'ERROR';
  buttonIconPosition?: 'LEFT' | 'RIGHT';
  buttonStyles?: any;
  isDisabled?: boolean;
  buttonTextClassName?: string;
}

export const AppDropdownButton: React.FC<Props> = ({
  options,
  onSelect,
  icon,
  buttonTitle,
  contentWidth = 200,
  buttonType = 'PRIMARY',
  buttonIconPosition = 'RIGHT',
  buttonStyles = {},
  isDisabled = false,
  buttonTextClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);
      const clickedOutsideButton = buttonRef.current && !buttonRef.current.contains(target);

      if (clickedOutsideDropdown && clickedOutsideButton) {
        setIsOpen(false);
      }
    };

    let animationFrameId: number;

    const updatePosition = () => {
      if (!isOpen || !buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();

      // Check if button is in viewport
      const isInViewport =
        rect.top >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.left >= 0 &&
        rect.right <= window.innerWidth;

      if (isInViewport) {
        // Get actual dropdown height if available
        const dropdownHeight = dropdownRef.current?.offsetHeight || 100;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Determine if dropdown should open upward or downward
        const shouldOpenUpward = spaceBelow < dropdownHeight + 20 && spaceAbove > spaceBelow;

        // Update dropdown position
        setDropdownPosition({
          top: shouldOpenUpward ? rect.top - dropdownHeight - 5 : rect.bottom + 10,
          left: rect.right - contentWidth,
        });

        // Continue checking
        animationFrameId = requestAnimationFrame(updatePosition);
      } else {
        // Button is out of viewport, close dropdown
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Start position tracking
      animationFrameId = requestAnimationFrame(updatePosition);
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);

      // Cancel animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isOpen, contentWidth]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: Option) => {
    onSelect(option);
    toggleDropdown();
  };

  return (
    <>
      <div className="relative" ref={buttonRef}>
        <AppButton
          text={buttonTitle}
          onClick={toggleDropdown}
          buttonStyles={{ height: '40px', ...buttonStyles }}
          icon={icon}
          iconPosition={buttonIconPosition}
          type={buttonType}
          isDisabled={isDisabled}
          buttonTextClassName={buttonTextClassName}
        />
      </div>
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: contentWidth,
              zIndex: 9999,
            }}
            className={`border PRIMARY_50-BG py-2 rounded shadow-xl`}>
            {options.map((option, i) => (
              <div
                key={`${option.value}_${i}`}
                onClick={() => handleOptionClick(option)}
                className="flex hover:bg-blue-100 hover:text-[#3762FB] px-1 py-2 cursor-default items-center">
                <div className="mr-2">{option.icon && option.icon}</div>
                <div className="truncate text-xs">{option.label}</div>
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};
